import React, {
  useCallback,
  useState,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  Divider,
  Collapse,
  Hidden,
  BottomNavigation,
  BottomNavigationAction,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import { FaHome, FaClipboardList } from 'react-icons/fa';
import { startOfYear } from 'date-fns';
import { ToastContainer } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/auth';
import { useWindowSize } from '../../hooks/windowSize';
import api from '../../services/api';

import Basemap, { INITIAL_SELECTED } from '../../components/maps/Basemap';
import CreateOrderButton from '../../components/buttons/CreateOrderButton';
import CustomDropdown from '../../components/selectors/CustomDropdown';
import DatePicker from '../../components/selectors/DatePicker';
import InfoCardWithDropdown from '../../components/cards/InfoCardWithDropdown';
import ToggleBottomBar from '../../components/nav/ToggleBottomBar';
import ToggleRightBar from '../../components/nav/ToggleRightBar';
import MobileDashboard from '../../components/layouts/MobileDashboard';
import RightBar from '../../components/layouts/RightBar';
import BottomBar from '../../components/layouts/BottomBar';
import KpisAndDf from './KpisAndDf';
import FailureAnalysis from './FailureAnalysis';
import MaintenabilityChart from './MaintenabilityChart';
import ReliabilityChart from './ReliabilityChart';
import AvailabilityChart from './AvailabilityChart';

import {
  calculateLossesProfile,
  calculateOrdersProfile,
  calculateAverageRoadCondition,
  getMttrVsFailsChartData,
  handleMissingData,
} from '../../utils/getChartsValues';

import { IState } from '../../store';
import { IOrdersState } from '../../store/modules/orders/types';
import { ISelectedFeature } from '../../types/layers';
import {
  IAlarmTypes,
  IGetAlarmsApi,
  IGetMttrVsFailsChartData,
  ILossesProfileReturn,
} from '../../types/kpis';
import { IOrderDetailed, IOrderForm } from '../../types/orders';
import { IKpisState } from '../../store/modules/kpis/types';
import { getKpisRequest } from '../../store/modules/kpis/actions';

import { useStyles } from './styles';
import 'react-toastify/dist/ReactToastify.css';
import { faLogEvent } from '../../services/firebase/analytics';
import { usePreferences } from '../../hooks/preferences';

interface IFormResponse {
  status: string;
  order_form: IOrderForm[];
}

interface IKpisChartData {
  status: string;
  data: number[];
}

const Availability: React.FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const { disableTabs, selectDataType } = usePreferences();
  const mapRef = useRef<any>();
  const { user, jwt, selectedUnit, selectUnit } = useAuth();
  const size = useWindowSize();
  const history = useHistory();
  const dispatch = useDispatch();
  const [selectedFeature, setSelectedFeature] = useState<ISelectedFeature>(
    INITIAL_SELECTED,
  );
  const [openUnitSelector, setOpenUnitSelector] = useState(false);
  const orders = useSelector<IState, IOrdersState>(appState => appState.orders);
  const kpisState = useSelector<IState, IKpisState>(appState => appState.kpis);
  const [finishedOrders, setFinishedOrders] = useState<IOrderDetailed[]>([]);
  const [unitFinishedOrders, setUnitFinishedOrders] = useState<
    IOrderDetailed[]
  >([]);
  const [filteredOrders, setFilteredOrders] = useState<IOrderDetailed[]>([]);
  const [ordersForms, setOrdersForms] = useState<IOrderForm[]>([]);
  const [alarms, setAlarms] = useState<IAlarmTypes[]>([]);
  const [isRightBarOpen, setIsRightBarOpen] = useState(true);
  const [isBottomBarOpen, setIsBottomBarOpen] = useState(true);
  const [showMobileContent, setShowMobileContent] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [timescale, setTimescale] = useState(t('components:selectors.monthly'));
  const [openTimescale, setOpenTimescale] = useState(false);
  const [mttrInfo, setMttrInfo] = useState({
    value: 0,
    meta: 0,
  });
  const [mtbfInfo, setMtbfInfo] = useState({
    value: 0,
    meta: 0,
  });
  const [df, setDf] = useState<number[]>([]);
  const [averageDf, setAverageDf] = useState<number>(0);
  const [mttrChartData, setMttrChartData] = useState<IKpisChartData>({
    status: '',
    data: [],
  });
  const [mtbfChartData, setMtbfChartData] = useState<IKpisChartData>({
    status: '',
    data: [],
  });
  const [failsData, setFailsData] = useState<number[]>([]);
  const [roadConditionsData, setRoadConditionsData] = useState<number[]>([]);
  const [
    mttrVsFailsChartData,
    setMttrVsFailsChartData,
  ] = useState<IGetMttrVsFailsChartData>({
    scatterData: [],
    sharpFails: [],
    noFails: [],
    chronicAndSharpFails: [],
    chronicFails: [],
  });
  const [lossesProfile, setLossesProfile] = useState<ILossesProfileReturn>({
    lossesProfileData: [],
    accumulatedTotal: [],
    accumulatedPercentage: [],
  });
  const [mobileChartsWidth, setMobileChartsWidth] = useState(300);
  const [selectedBottomData, setSelectedBottomData] = useState<string>(
    'mantenability',
  );

  const handleClearDates = (): void => {
    setStartDate(null);
    setEndDate(null);
  };

  const getSelectedUnitData = useCallback(() => {
    if (selectedUnit.id === 'itabira') {
      setUnitFinishedOrders(finishedOrders);
    } else {
      const unitData = user.units.find(unit => unit.name === selectedUnit.name);
      if (unitData) {
        setUnitFinishedOrders(
          finishedOrders.filter(order => order.unit_id === unitData.id),
        );
      }
    }
  }, [selectedUnit, finishedOrders, user.units]);

  const handleGetAlarms = useCallback(() => {
    const unitsIds: string[] = [];
    user.units.map(unit => unitsIds.push(unit.id));

    api
      .get<IGetAlarmsApi>(
        `/dashboard/orders/form/alarm/FINALIZADA,INCOMPLETA/2021-01-01/${unitsIds}`,
        {
          headers: {
            'x-access-token': jwt,
          },
        },
      )
      .then(response => {
        setAlarms(response.data.data);
      });
  }, [jwt, user.units]);

  const handleGetOrdersForms = useCallback(() => {
    const unitsIds: string[] = [];
    user.units.map(unit => unitsIds.push(unit.id));

    api
      .get<IFormResponse>(
        `/orders/form/2021-01-01/FINALIZADA,INCOMPLETA/${unitsIds}`,
        {
          headers: {
            'x-access-token': jwt,
          },
        },
      )
      .then(response => {
        setOrdersForms(response.data.order_form);
      });
  }, [jwt, user.units]);

  const handleClearSelectedFeature = useCallback(() => {
    mapRef.current.clearFeatures();
  }, []);

  const formattedTimescale = useMemo((): string => {
    switch (timescale) {
      case t('components:selectors.monthly'):
        return 'month';
      case t('components:selectors.weekly'):
        return 'week';
      case t('components:selectors.yearly'):
        return 'year';
      default:
        return 'month';
    }
  }, [timescale, t]);

  const handleSelectUnit = useCallback(
    (unitName: string) => {
      if (unitName.toLowerCase() === 'complexo de itabira') {
        selectUnit('itabira');
        return;
      }

      const selected = user.units.find(unit => unit.name === unitName);
      if (selected) {
        selectUnit(selected.id);
      }
    },
    [selectUnit, user.units],
  );

  const handleOpenBottomBar = useCallback((value: number) => {
    if (value === 0) {
      setSelectedBottomData('mantenability');
    } else if (value === 1) {
      setSelectedBottomData('reliability');
    } else if (value === 2) {
      setSelectedBottomData('availability');
    }
    setIsBottomBarOpen(true);
  }, []);

  const handleClickCreateOrder = (): void => {
    faLogEvent('CREATE_ORDER_FROM_AVAILABILITY_SCREEN');
    history.push('/dashboard/monitoramento/ordens-de-servico', {
      shouldCreateOrder: true,
    });
  };

  // GET ORDERS FROM STATE
  useEffect(() => {
    if (orders) {
      setFinishedOrders(orders.finishedOrders);
    }
  }, [orders]);

  // Get Api Data
  useEffect(() => {
    if (user.organization && user.units.length > 0) {
      handleGetOrdersForms();
      handleGetAlarms();
    }
  }, [user.organization, user.units, handleGetOrdersForms, handleGetAlarms]);

  // Handle get kpi by dates
  useEffect(() => {
    if (startDate && endDate) {
      dispatch(
        getKpisRequest({
          user,
          jwt,
          startDate,
          endDate,
          timescale: formattedTimescale,
        }),
      );
    } else {
      const today = new Date();
      const firstDayOfYear = startOfYear(today);
      dispatch(
        getKpisRequest({
          user,
          jwt,
          startDate: firstDayOfYear,
          endDate: today,
          timescale: formattedTimescale,
        }),
      );
    }
  }, [startDate, endDate, dispatch, formattedTimescale, user, jwt]);

  // Filter orders by selected feature
  useEffect(() => {
    if (selectedFeature.name !== '') {
      if (selectedFeature.type === 'fill') {
        const select = unitFinishedOrders.filter(
          order => order.region === selectedFeature.name,
        );
        setFilteredOrders(select);
      } else if (selectedFeature.type === 'line') {
        const select = unitFinishedOrders.filter(
          order => order.road === selectedFeature.name,
        );
        setFilteredOrders(select);
      }
    } else {
      setFilteredOrders(unitFinishedOrders);
    }
  }, [selectedFeature, unitFinishedOrders, selectedUnit]);

  // Calculate orders profile
  useEffect(() => {
    if (alarms.length > 0) {
      calculateOrdersProfile(alarms);
    }
  }, [alarms]);

  // Calculate kpi data
  useEffect(() => {
    if (kpisState.kpis.length > 0) {
      if (selectedFeature.unit_id !== '') {
        const selectedKpiData = kpisState.kpis.find(
          kpi => kpi.unit_id === selectedFeature.unit_id,
        );
        if (selectedKpiData) {
          const formattedMttr = handleMissingData(selectedKpiData.mttr);
          const formattedMtbf = handleMissingData(selectedKpiData.mtbf);
          const averageRoadConditions = calculateAverageRoadCondition(
            selectedKpiData,
          );
          setMttrChartData({
            status: formattedMttr.status,
            data: formattedMttr.formattedData,
          });
          setMtbfChartData({
            status: formattedMtbf.status,
            data: formattedMtbf.formattedData,
          });
          setRoadConditionsData(averageRoadConditions);
          setFailsData(selectedKpiData.order_count);
        }
      } else {
        const selectedKpiData = kpisState.kpis.find(kpi => {
          if (selectedUnit.id === 'itabira') {
            return kpi.unit_id === user.units[1].id;
          }
          return kpi.unit_id === selectedUnit.id;
        });
        if (selectedKpiData) {
          const formattedMttr = handleMissingData(selectedKpiData.mttr);
          const formattedMtbf = handleMissingData(selectedKpiData.mtbf);
          const averageRoadConditions = calculateAverageRoadCondition(
            selectedKpiData,
          );
          const dfValues = formattedMtbf.formattedData.map((val, idx) => {
            if (val === 0) {
              return 0;
            }
            return val / (val + formattedMttr.formattedData[idx]);
          });
          setDf(dfValues);
          setMttrChartData({
            status: formattedMttr.status,
            data: formattedMttr.formattedData,
          });
          setMtbfChartData({
            status: formattedMtbf.status,
            data: formattedMtbf.formattedData,
          });
          setFailsData(selectedKpiData.order_count);
          setRoadConditionsData(averageRoadConditions);
        }
      }
    }
  }, [kpisState.kpis, selectedFeature, user.units, selectedUnit.id]);

  // Generate kpis charts input data
  useEffect(() => {
    if (kpisState.averages.length > 0) {
      if (selectedFeature.unit_id !== '') {
        const selectedKpiData = kpisState.averages.find(
          kpi => kpi.unit_id === selectedFeature.unit_id,
        );
        setMttrInfo({
          value: selectedKpiData ? selectedKpiData.averageMttr : 0,
          meta: selectedKpiData ? selectedKpiData.averageMttr : 0,
        });
        setMtbfInfo({
          value: selectedKpiData ? selectedKpiData.averageMtbf : 0,
          meta: selectedKpiData ? selectedKpiData.averageMtbf : 0,
        });
        setAverageDf(selectedKpiData ? selectedKpiData.averageDf : 0);
      } else {
        const selectedKpiData = kpisState.averages.find(
          kpi => kpi.unit_id === user.units[1].id,
        );
        setMttrInfo({
          value: selectedKpiData ? selectedKpiData.averageMttr : 0,
          meta: selectedKpiData ? selectedKpiData.averageMttr : 0,
        });
        setMtbfInfo({
          value: selectedKpiData ? selectedKpiData.averageMtbf : 0,
          meta: selectedKpiData ? selectedKpiData.averageMtbf : 0,
        });
        setAverageDf(selectedKpiData ? selectedKpiData.averageDf : 0);
      }
    }
  }, [kpisState.averages, selectedFeature, user.units]);

  // Generate MTTR x Fails chart data
  useEffect(() => {
    if (mttrChartData.data.length > 0 && failsData.length > 0) {
      const result = getMttrVsFailsChartData(mttrChartData.data, failsData);
      setMttrVsFailsChartData(result);
    }
  }, [mttrChartData, failsData]);

  // Calculate losses profile
  useEffect(() => {
    if (ordersForms.length > 0) {
      setLossesProfile(calculateLossesProfile(ordersForms));
    }
  }, [ordersForms]);

  // Handle charts width on mobile
  useEffect(() => {
    if (size.width) {
      if (size.width < 960 && size.width > 600) {
        setMobileChartsWidth(size.width * 0.56);
      } else if (size.width <= 600) {
        setMobileChartsWidth(size.width * 0.96);
      }
    }
  }, [size.width]);

  // Handle selected unit
  useEffect(() => {
    getSelectedUnitData();
  }, [getSelectedUnitData]);

  // HANDLE ASSETS TAB BAR INITIAL VALUES
  useEffect(() => {
    selectDataType('roads');
    disableTabs({
      roads: false,
      trucks: true,
      drills: true,
    });
  }, [selectDataType, disableTabs]);

  return (
    <div className={classes.container}>
      <ToastContainer className={classes.toastContainer} />
      {/* Basemap */}
      <Basemap
        ref={mapRef}
        isCreatingOrder={false}
        orders={filteredOrders}
        selectedFeatureName={select => setSelectedFeature(select)}
      />

      {/* Create order button */}
      <div className={classes.createOrderButton}>
        <CreateOrderButton
          height={48}
          onClick={() => handleClickCreateOrder()}
        />
      </div>

      <Hidden smDown>
        {/* Right side tool bar */}
        {!isRightBarOpen && (
          <ToggleRightBar
            isCreatingOrder={false}
            selectedFeatureName={selectedFeature.name}
            onClickOpen={() => setIsRightBarOpen(true)}
            onClickClear={handleClearSelectedFeature}
          />
        )}

        {/* Bottom tool bar */}
        {!isBottomBarOpen && (
          <ToggleBottomBar
            isCreatingOrder={false}
            buttons={[
              t('screens:availability.maintainability'),
              t('screens:availability.reliability'),
              t('screens:availability.title'),
            ]}
            onClickOpen={() => setIsBottomBarOpen(true)}
            onClickTab={(value: number) => handleOpenBottomBar(value)}
          />
        )}

        <Collapse in={isRightBarOpen}>
          {/* Right content: open orders info and charts */}
          <RightBar onCloseRightBar={() => setIsRightBarOpen(false)}>
            <div>
              <InfoCardWithDropdown
                title="identificação"
                open={openUnitSelector}
                text={selectedUnit.name}
                labels={[
                  'Complexo de Itabira',
                  ...user.units.map(unit => unit.name),
                ]}
                labelFontsize={18}
                toggle={() => setOpenUnitSelector(!openUnitSelector)}
                onClickItem={(event: any) =>
                  handleSelectUnit(event.target.innerText)
                }
              />

              <div className={classes.datePickerContainer}>
                <DatePicker
                  id="availability-picker"
                  startDate={startDate}
                  endDate={endDate}
                  onChangeStartDate={(date: Date | null) => setStartDate(date)}
                  onChangeEndDate={(date: Date | null) => setEndDate(date)}
                  onClickClearDate={handleClearDates}
                  marginBottom={0}
                  fontSize={10}
                  marginTop={8}
                />
                <CustomDropdown
                  open={openTimescale}
                  text={timescale}
                  showBorder
                  labels={[
                    t('components:selectors.monthly'),
                    t('components:selectors.weekly'),
                    t('components:selectors.yearly'),
                  ]}
                  toggle={() => setOpenTimescale(!openTimescale)}
                  onClickItem={(event: any) =>
                    setTimescale(event.target.innerText)
                  }
                  labelFontsize={10}
                  paddingHorizontal={8}
                  iconSize={16}
                  marginTop={8}
                />
              </div>
            </div>
            <Divider />
            <KpisAndDf
              mttrData={mttrInfo}
              mtbfData={mtbfInfo}
              dfData={averageDf}
            />
            <Divider />
            <div className={classes.failureAnalysisContainer}>
              {mttrVsFailsChartData.scatterData.length > 0 && (
                <FailureAnalysis data={mttrVsFailsChartData} />
              )}
            </div>
          </RightBar>
        </Collapse>

        {/* Bottom Bar Content */}
        <Collapse in={isBottomBarOpen} timeout={0}>
          <BottomBar
            selected={selectedBottomData}
            onSelect={select => setSelectedBottomData(select)}
            onCloseBottomBar={() => setIsBottomBarOpen(false)}
            bottomButtonsLabels={[
              {
                label: t('screens:availability.maintainability'),
                value: 'mantenability',
              },
              {
                label: t('screens:availability.reliability'),
                value: 'reliability',
              },
              { label: t('screens:availability.title'), value: 'availability' },
            ]}
            bottomBarComponents={[
              mttrChartData.data.length > 0 && failsData.length > 0 && (
                <MaintenabilityChart
                  mttrData={mttrChartData}
                  failureData={failsData}
                  kpiLabels={kpisState.labels}
                  width={size.width ? size.width * 0.6 : '100%'}
                />
              ),
              mtbfChartData.data.length > 0 &&
                roadConditionsData.length > 0 && (
                  <ReliabilityChart
                    mtbfData={mtbfChartData}
                    roadsConditions={roadConditionsData}
                    kpiLabels={kpisState.labels}
                    width={size.width ? size.width * 0.6 : '100%'}
                  />
                ),
              df.length > 0 && kpisState.labels.length > 0 && (
                <AvailabilityChart
                  dfData={df}
                  kpiLabels={kpisState.labels}
                  width={size.width ? size.width * 0.6 : '100%'}
                />
              ),
            ]}
          />
        </Collapse>
      </Hidden>

      {/* Bottom tab bar, only on xs and sm screens */}
      <Hidden mdUp>
        <BottomNavigation showLabels className={classes.bottomNavigation}>
          <BottomNavigationAction
            className={classes.bottomNavLabel}
            label={t('screens:availability.home')}
            onClick={() => history.push('/dashboard/monitoramento')}
            icon={<FaHome size={24} color={theme.palette.common.white} />}
          />
          <BottomNavigationAction
            className={classes.bottomNavLabel}
            label={t('screens:availability.title')}
            onClick={() => setShowMobileContent(true)}
            icon={
              <FaClipboardList size={24} color={theme.palette.common.white} />
            }
          />
        </BottomNavigation>

        {/* Mobile availability content */}
        {showMobileContent && (
          <MobileDashboard
            showMobileContent={() => setShowMobileContent(!showMobileContent)}
          >
            <InfoCardWithDropdown
              title={t('screens:availability.identification')}
              open={openUnitSelector}
              text={selectedUnit.name}
              labels={user.units.map(unit => unit.name)}
              labelFontsize={18}
              toggle={() => setOpenUnitSelector(!openUnitSelector)}
              onClickItem={(event: any) =>
                handleSelectUnit(event.target.innerText)
              }
            />
            <Divider />
            <KpisAndDf
              mttrData={mttrInfo}
              mtbfData={mtbfInfo}
              dfData={averageDf}
            />
            <Divider />
            {mttrVsFailsChartData.scatterData.length > 0 && (
              <FailureAnalysis
                data={mttrVsFailsChartData}
                height={size.height ? size.height * 0.32 : '100%'}
              />
            )}
            <Divider />
            {mttrChartData.data.length > 0 && failsData.length > 0 && (
              <MaintenabilityChart
                mttrData={mttrChartData}
                failureData={failsData}
                kpiLabels={kpisState.labels}
                width={mobileChartsWidth}
                height={size.height ? size.height * 0.25 : '100%'}
                showTitle
              />
            )}
            <Divider />
            {mtbfChartData.data.length > 0 && roadConditionsData.length > 0 && (
              <ReliabilityChart
                showTitle
                mtbfData={mtbfChartData}
                roadsConditions={roadConditionsData}
                kpiLabels={kpisState.labels}
                width={mobileChartsWidth}
                height={size.height ? size.height * 0.25 : '100%'}
              />
            )}
            <Divider />
            {df.length > 0 && kpisState.labels.length > 0 && (
              <AvailabilityChart
                showTitle
                dfData={df}
                kpiLabels={kpisState.labels}
                width={size.width ? size.width * 0.25 : '100%'}
                margin={48}
              />
            )}
          </MobileDashboard>
        )}
      </Hidden>
    </div>
  );
};

export default Availability;
